import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBase } from '../utils/api';

/**
 * ExecutiveApprove - Token-based executive approval page (accessed via email link).
 * Org admins can approve or reject a pending executive from here.
 */
const ExecutiveApprove = () => {
  const { token } = useParams();
  const [executive, setExecutive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null); // 'approved' | 'rejected'
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    const fetchExecutive = async () => {
      try {
        const apiBase = getApiBase();
        const response = await axios.get(`${apiBase}/executives/approve/${token}`);
        setExecutive(response.data.executive);
      } catch (err) {
        const detail = err.response?.data?.detail || err.message;
        if (err.response?.status === 410) {
          setError('This approval link has expired. Please ask the EA to re-submit the executive request.');
        } else if (err.response?.status === 404) {
          setError('This approval link is invalid or has already been used.');
        } else if (err.response?.status === 400) {
          setError(detail);
        } else {
          setError('Failed to load executive details. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchExecutive();
  }, [token]);

  const handleApprove = async () => {
    setProcessing(true);
    setError('');
    try {
      const apiBase = getApiBase();
      await axios.post(`${apiBase}/executives/approve/${token}`);
      setResult('approved');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve executive');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    setError('');
    try {
      const apiBase = getApiBase();
      await axios.post(`${apiBase}/executives/reject/${token}`, {
        reason: rejectionReason
      });
      setResult('rejected');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reject executive');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${result === 'approved' ? 'bg-green-100' : 'bg-red-100'}`}>
            {result === 'approved' ? (
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Executive {result === 'approved' ? 'Approved' : 'Rejected'}
          </h2>
          <p className="text-gray-600">
            {result === 'approved'
              ? `${executive?.full_name || 'The executive'} has been approved and is now active. The requesting EA has been notified and assigned.`
              : `${executive?.full_name || 'The executive'} has been rejected. The requesting EA has been notified.`}
          </p>
        </div>
      </div>
    );
  }

  if (error && !executive) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Process</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 px-8 py-6">
          <h1 className="text-xl font-semibold text-white">Executive Approval Request</h1>
          <p className="text-blue-100 text-sm mt-1">Review and approve or reject this new executive</p>
        </div>

        {/* Executive Details */}
        <div className="px-8 py-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Executive Details</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-500">Name:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {executive?.full_name || `${executive?.first_name || ''} ${executive?.last_name || ''}`.trim()}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Email:</span>
                  <span className="ml-2 text-sm text-gray-900">{executive?.email}</span>
                </div>
                {executive?.title && (
                  <div>
                    <span className="text-sm text-gray-500">Title:</span>
                    <span className="ml-2 text-sm text-gray-900">{executive.title}</span>
                  </div>
                )}
                {executive?.company && (
                  <div>
                    <span className="text-sm text-gray-500">Company:</span>
                    <span className="ml-2 text-sm text-gray-900">{executive.company}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Requested By</h3>
              <p className="text-sm text-gray-900">{executive?.created_by_user_name || 'Unknown'}</p>
            </div>
          </div>

          {/* Actions */}
          {!rejecting ? (
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleApprove}
                disabled={processing}
                className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? 'Processing...' : 'Approve'}
              </button>
              <button
                onClick={() => setRejecting(true)}
                disabled={processing}
                className="flex-1 px-4 py-2.5 bg-white text-red-600 border border-red-300 rounded-md font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Reject
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Reason for rejection (optional)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                placeholder="Enter reason..."
              />
              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  disabled={processing}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {processing ? 'Processing...' : 'Confirm Rejection'}
                </button>
                <button
                  onClick={() => { setRejecting(false); setRejectionReason(''); }}
                  disabled={processing}
                  className="px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-md font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutiveApprove;
